/**
 * The orcaloops.ai approve-page RPC definitions (OL-360).
 *
 * `/oauth/authorize` is stateless: it validates the request and sends the
 * browser to `https://orcaloops.ai/connect?<canonical OAuth query>`. That page
 * calls `oauthConsent.describe` to render the prompt and `oauthConsent.decide`
 * to turn **Approve** into a single-use authorization code.
 *
 * **Clerk session only.** Both handlers refuse an `ocl_` token-authed actor
 * before any database read, so a leaked token cannot approve a grant and mint
 * itself a second credential.
 *
 * **No payload carries a person id.** The subject is always
 * `LoopActor.person.id`; `customerId` on `decide` is only a selector among the
 * caller's own memberships.
 *
 * Client-safe: this file imports only `@effect/rpc`, `effect`, the error
 * classes and the middleware TAG.
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { Schema } from "effect";

import { DatabaseError, Forbidden, Unauthorized, ValidationError } from "../errors.ts";
import { LoopActorMiddleware } from "../loop-actor.tags.ts";

/**
 * The raw `/connect` query string, without the leading `?`. Re-validated on
 * every call: a crafted URL can skip `/oauth/authorize`.
 *
 * @since 1.0.0
 */
export const OAuthConsentQueryInput = Schema.Struct({ query: Schema.String });

/**
 * @since 1.0.0
 */
export type OAuthConsentQueryInput = Schema.Schema.Type<typeof OAuthConsentQueryInput>;

/**
 * One of the caller's own workspaces, offered as the grant's customer.
 *
 * @since 1.0.0
 */
export const OAuthConsentCustomer = Schema.Struct({ id: Schema.String, name: Schema.String });

/**
 * @since 1.0.0
 */
export type OAuthConsentCustomer = Schema.Schema.Type<typeof OAuthConsentCustomer>;

/**
 * What the approve page renders.
 *
 * @since 1.0.0
 */
export const OAuthConsentView = Schema.Struct({
  clientName: Schema.String,
  /** `new URL(redirectUri).host`, e.g. `localhost:53682`. */
  redirectHost: Schema.String,
  /** `noWorkspace` = zero memberships: the page offers no Approve. */
  status: Schema.Literal("ready", "noWorkspace"),
  customers: Schema.Array(OAuthConsentCustomer),
});

/**
 * @since 1.0.0
 */
export type OAuthConsentView = Schema.Schema.Type<typeof OAuthConsentView>;

/**
 * The person's answer to the prompt.
 *
 * @since 1.0.0
 */
export const OAuthConsentDecideInput = Schema.Struct({
  query: Schema.String,
  decision: Schema.Literal("approve", "deny"),
  /** Selector among the caller's memberships; may be absent only with exactly one. */
  customerId: Schema.optional(Schema.String),
});

/**
 * @since 1.0.0
 */
export type OAuthConsentDecideInput = Schema.Schema.Type<typeof OAuthConsentDecideInput>;

/**
 * Where the browser goes next: the client's loopback redirect with `code` (or
 * `error=access_denied`), `state` and `iss`.
 *
 * @since 1.0.0
 */
export const OAuthConsentDecision = Schema.Struct({ redirectTo: Schema.String });

/**
 * @since 1.0.0
 */
export type OAuthConsentDecision = Schema.Schema.Type<typeof OAuthConsentDecision>;

/**
 * The approve-page RPC group. Mounted separately (OL-363).
 *
 * @since 1.0.0
 */
export class OAuthConsentRpcs extends RpcGroup.make(
  Rpc.make("oauthConsent.describe", {
    payload: OAuthConsentQueryInput,
    success: OAuthConsentView,
    error: Schema.Union(Unauthorized, Forbidden, ValidationError, DatabaseError),
  }).middleware(LoopActorMiddleware),
  Rpc.make("oauthConsent.decide", {
    payload: OAuthConsentDecideInput,
    success: OAuthConsentDecision,
    error: Schema.Union(Unauthorized, Forbidden, ValidationError, DatabaseError),
  }).middleware(LoopActorMiddleware),
) {}
